import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { moveItemInArray } from '@angular/cdk/drag-drop';
import {
  SearchFilter,
  IFilterResponse,
} from "../../../infrastructure/ifilter-component";
import { MatDialog } from "@angular/material";
import { SearchFilterComponent } from "../../search-filter/search-filter.component";
import { PagerControlComponent } from "../../controls/pager/pager-control.component";
import { PreBillingSearchItem } from "../../../model/billing/pre-billing-search-item";
import { Router } from "@angular/router";
import { InvoiceType } from "../../../enums/invoice-type.enum";
import { MenuService } from "../../../services/menu.service";
import { AuthService } from "../../../services/auth.service";
import { PreBillingService } from "../../../services/billing/pre-billing.service";
import { NotificationToastService } from "src/app/services/notification-toast.service";
import { ReportViewerDialogComponent } from "../../dialogs/report-viewer/report-viewer-dialog/report-viewer-dialog.component";
import { ScheduledTask } from "../../../enums/escheduled-task.enum";
import { BillingCloseTaskDialogComponent } from "../../dialogs/billing-close-task-dialog/billing-close-task-dialog.component";
import { BillingManualListDialogComponent } from "../../dialogs/billing-manual-list-dialog/billing-manual-list-dialog.component";
import { ConfirmDialogService } from "src/app/services/confirm-dialog.service";

@Component({
  selector: "app-pre-billing-search",
  templateUrl: "./pre-billing-search.component.html",
  styleUrls: ["./pre-billing-search.component.css"],
})
export class PreBillingSearchComponent implements OnInit, OnDestroy {

  private readonly sessionColumnsKey = 'pre-billing-search.columns.v1';
  private readonly sessionSortKey = 'pre-billing-search.sort';
  private readonly sessionColumnWidthsKey = 'pre-billing-search.column-widths.v1';
  private hasStoredColumnsState = false;

  displayedColumns: string[] = [
    'Id', 'ClientCode', 'Date', 'Amount', 'BillingGroupName',
    'BillingGroupFrom', 'BillingGroupTo', 'BillingGroupNumber', 'Star'
  ];

  readonly columnsDefinition: Array<{ field: string; title: string }> = [
    { field: 'Id', title: 'Número' },
    { field: 'ClientCode', title: 'Código cliente' },
    { field: 'Date', title: 'Fecha' },
    { field: 'Amount', title: 'Importe' },
    { field: 'BillingGroupName', title: 'Corte' },
    { field: 'BillingGroupFrom', title: 'Corte inicio' },
    { field: 'BillingGroupTo', title: 'Corte fin' },
    { field: 'BillingGroupNumber', title: 'Corte número' },
  ];

  searchFilters: SearchFilter[];
  draggedColumn: string | null = null;
  sortActive: string = '';
  sortDirection: string = '';
  allItems: PreBillingSearchItem[] = [];
  columnFilters: { [key: string]: string } = {};
  columnWidths: { [key: string]: number } = {};
  dataSource: PreBillingSearchItem[] = [];
  loading: boolean;

  @ViewChild("searchFilter", { static: false }) searchFilter: SearchFilterComponent;
  @ViewChild("pager", { static: true }) pager: PagerControlComponent;

  get exportColumns(): string[] {
    return this.orderedColumnsDefinition.map(col => col.field);
  }

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private preBillingService: PreBillingService,
    private authService: AuthService,
    private confirmService: ConfirmDialogService,
    private notificationToastService: NotificationToastService,
    private menuService: MenuService
  ) {
    this.menuService.setMenuTitle("Generación de factura");
  }

  ngOnInit() {
    this.restoreColumnsOrder();
    this.normalizeDisplayedColumns();
    this.restoreSortState();
    this.restoreColumnWidths();
  }

  ngOnDestroy() {
    this.removeResizeListeners();
  }

  selectedPageIndexChange(pagedItems: any[]) {
    this.dataSource = pagedItems ? [...pagedItems] : [];
    this.loading = false;
  }

  onEndSearch(response: IFilterResponse<PreBillingSearchItem>) {
    this.searchFilters = response && response.searchFilters ? response.searchFilters : [];
    this.allItems = response && response.items ? [...response.items] : [];
    this.refreshGrid();
  }

  onStartSearch() {
    this.loading = true;
  }

  async viewDetail(preBillingSearchItem: PreBillingSearchItem) {
    try {
      preBillingSearchItem.Loading = true;
      const reportViewer = await this.preBillingService.getPreBillingReport(
        preBillingSearchItem.Id,
        this.authService.getCenterId()
      );

      this.dialog.open(ReportViewerDialogComponent, {
        width: "90%",
        height: "600px",
        data: reportViewer,
      });
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Facturación"
      );
    } finally {
      preBillingSearchItem.Loading = false;
    }
  }

  redirectCreateInvoice(preBillingSearchItem: PreBillingSearchItem) {
    this.router.navigate([
      "billing-invoice-entity-edit",
      preBillingSearchItem.Id,
      InvoiceType.Invoice,
    ]);
  }

  redirectCreateDebit(preBillingSearchItem: PreBillingSearchItem) {
    this.router.navigate([
      "billing-invoice-entity-edit",
      preBillingSearchItem.Id,
      InvoiceType.Debit,
    ]);
  }

  asociateInvoice(preBillingSearchItem: PreBillingSearchItem) {
    const dialog = this.dialog.open(BillingManualListDialogComponent, {
      width: "90%",
      height: "400px",
    });

    dialog.afterClosed().subscribe(async (result) => {
      if (result) {
        try {
          const confPromise =
            result.Amount !== preBillingSearchItem.Amount
              ? this.confirmService
                  .confirm(
                    "Facturación",
                    "El importe de la prefactura es distinto al de la factura manual. Desea continuar?",
                    "Aceptar",
                    "Cancelar"
                  )
                  .toPromise()
              : Promise.resolve(true);

          if (await confPromise) {
            preBillingSearchItem.Loading = true;
            await this.preBillingService.asociateBillingManual(
              preBillingSearchItem.Id,
              result.InvoiceId
            );
            this.notificationToastService.success(
              "Factura manual asociada correctamente",
              "Facturación"
            );
            this.searchFilter.search();
          }
        } catch (err) {
          this.notificationToastService.error(
            JSON.stringify(err.error),
            "Facturación"
          );
        } finally {
          preBillingSearchItem.Loading = false;
        }
      }
    });
  }

  async exportImages(
    preBillingSearchItem: PreBillingSearchItem,
    agruparPorPlan: boolean = false,
    agruparPorPlanYContraste: boolean = false
  ) {
    try {
      preBillingSearchItem.Loading = true;
      const ids =
        await this.preBillingService.getMedicalAppointmentProcedureIds(
          preBillingSearchItem.Id
        );

      this.dialog.open(BillingCloseTaskDialogComponent, {
        width: "50%",
        data: {
          escheduledTaskId: ScheduledTask.billingExportImages,
          taskName: `${this.authService
            .getUserName()
            .toUpperCase()} - EXPORTACION-IMAGENES`,
          medicalAppointmentProcedureIds: ids,
          agruparPorPlan: agruparPorPlan,
          agruparPorPlanYContraste: agruparPorPlanYContraste,
        },
      });
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Facturación"
      );
    } finally {
      preBillingSearchItem.Loading = false;
    }
  }

  async exportStudyReport(
    preBillingSearchItem: PreBillingSearchItem,
    agruparPorPlan: boolean = false,
    agruparPorPlanYContraste: boolean = false
  ) {
    try {
      preBillingSearchItem.Loading = true;
      const ids =
        await this.preBillingService.getMedicalAppointmentProcedureIds(
          preBillingSearchItem.Id
        );

      this.dialog.open(BillingCloseTaskDialogComponent, {
        width: "50%",
        data: {
          escheduledTaskId: ScheduledTask.billingExportStudyReport,
          taskName: `${this.authService
            .getUserName()
            .toUpperCase()} - EXPORTACION-INFORMES`,
          medicalAppointmentProcedureIds: ids,
          agruparPorPlan: agruparPorPlan,
          agruparPorPlanYContraste: agruparPorPlanYContraste,
        },
      });
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Facturación"
      );
    } finally {
      preBillingSearchItem.Loading = false;
    }
  }

  formatCellValue(element: PreBillingSearchItem, field: string): string {
    const value = (element as any)[field];
    if (value === null || value === undefined) { return ''; }
    if (this.isDateField(field)) {
      const d = new Date(value);
      if (isNaN(d.getTime())) { return String(value); }
      const day = d.getDate().toString().padStart(2, '0');
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      return `${day}/${month}/${d.getFullYear()}`;
    }
    return String(value);
  }

  private isDateField(field: string): boolean {
    return field === 'Date' || field === 'BillingGroupFrom' || field === 'BillingGroupTo';
  }

  get availableColumns(): Array<{ field: string; title: string }> {
    return this.columnsDefinition;
  }

  get orderedColumnsDefinition(): Array<{ field: string; title: string }> {
    const order = this.displayedColumns.filter(col => col !== 'Star');
    const visibleColumns = new Set(order);
    const orderMap = new Map<string, number>(order.map((col, idx) => [col, idx]));
    return this.columnsDefinition
      .filter(col => visibleColumns.has(col.field))
      .sort((a, b) => {
        const aIdx = orderMap.has(a.field) ? orderMap.get(a.field) : 999;
        const bIdx = orderMap.has(b.field) ? orderMap.get(b.field) : 999;
        return aIdx - bIdx;
      });
  }

  isColumnVisible(column: string): boolean {
    return this.displayedColumns.indexOf(column) >= 0;
  }

  setColumnVisibility(column: string, visible: boolean) {
    const dataColumns = this.displayedColumns.filter(col => col !== 'Star');
    if (visible) {
      if (dataColumns.indexOf(column) < 0) { dataColumns.push(column); }
    } else {
      if (dataColumns.length <= 1) { return; }
      const idx = dataColumns.indexOf(column);
      if (idx >= 0) { dataColumns.splice(idx, 1); }
      if (this.sortActive === column) {
        this.sortActive = '';
        this.sortDirection = '';
        sessionStorage.removeItem(this.sessionSortKey);
      }
      if (this.columnFilters[column]) { delete this.columnFilters[column]; }
    }
    this.displayedColumns = [...dataColumns, 'Star'];
    this.saveColumnsOrder();
    this.refreshGrid();
  }

  showAllColumns() {
    const allColumns = this.availableColumns.map(col => col.field);
    this.displayedColumns = [...allColumns, 'Star'];
    this.saveColumnsOrder();
    this.refreshGrid();
  }

  sortByColumn(column: string) {
    if (this.sortActive !== column) { this.sortAllData({ active: column, direction: 'asc' }); return; }
    if (this.sortDirection === 'asc') { this.sortAllData({ active: column, direction: 'desc' }); return; }
    this.sortAllData({ active: '', direction: '' });
  }

  getSortIcon(column: string): string {
    if (this.sortActive !== column) { return 'unfold_more'; }
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  sortAllData(sortEvent: any) {
    if (!sortEvent || !sortEvent.active || sortEvent.direction === '') {
      this.sortActive = ''; this.sortDirection = '';
      sessionStorage.removeItem(this.sessionSortKey);
      this.refreshGrid(); return;
    }
    this.sortActive = sortEvent.active;
    this.sortDirection = sortEvent.direction;
    this.saveSortState();
    this.refreshGrid();
  }

  onColumnFilterInput(column: string, value: string) {
    this.columnFilters[column] = value;
    this.refreshGrid();
  }

  getColumnWidth(column: string): number | null {
    const width = this.columnWidths[column];
    return width && width > 0 ? width : null;
  }

  startResize(event: MouseEvent, column: string) {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const headerCell = (event.target as HTMLElement).closest('th');
    const measuredWidth = headerCell ? Math.round(headerCell.getBoundingClientRect().width) : 100;
    const startWidth = this.getColumnWidth(column) || Math.max(80, measuredWidth);
    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      this.columnWidths = { ...this.columnWidths, [column]: Math.max(80, startWidth + delta) };
    };
    const onMouseUp = () => { this.removeResizeListeners(); this.saveColumnWidths(); };
    (window as any).__preBillingResizeMove = onMouseMove;
    (window as any).__preBillingResizeUp = onMouseUp;
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  onHeaderDragStart(event: DragEvent, column: string) {
    this.draggedColumn = column;
    if (event.dataTransfer) { event.dataTransfer.setData('text/plain', column); event.dataTransfer.effectAllowed = 'move'; }
  }

  onHeaderDragOver(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) { event.dataTransfer.dropEffect = 'move'; }
  }

  onHeaderDrop(event: DragEvent, targetColumn: string) {
    event.preventDefault();
    if (!this.draggedColumn || this.draggedColumn === targetColumn) { return; }
    const dataColumns = this.displayedColumns.filter(col => col !== 'Star');
    const sourceIndex = dataColumns.indexOf(this.draggedColumn);
    const targetIndex = dataColumns.indexOf(targetColumn);
    if (sourceIndex < 0 || targetIndex < 0) { this.draggedColumn = null; return; }
    moveItemInArray(dataColumns, sourceIndex, targetIndex);
    this.displayedColumns = [...dataColumns, 'Star'];
    this.saveColumnsOrder();
    this.draggedColumn = null;
  }

  onHeaderDragEnd() { this.draggedColumn = null; }

  private getSortableValue(item: PreBillingSearchItem, property: string): any {
    const value = (item as any)[property];
    if (value === null || value === undefined) { return ''; }
    if (this.isDateField(property)) { return new Date(value).getTime() || 0; }
    return typeof value === 'string' ? value.toLocaleLowerCase() : value;
  }

  private saveColumnsOrder() {
    sessionStorage.setItem(this.sessionColumnsKey, JSON.stringify(this.displayedColumns));
  }

  private restoreColumnsOrder() {
    const stored = sessionStorage.getItem(this.sessionColumnsKey);
    if (!stored) { return; }
    try {
      const parsed: string[] = JSON.parse(stored);
      if (!parsed || !Array.isArray(parsed)) { return; }
      const allowed = new Set(this.availableColumns.map(col => col.field));
      const parsedDataColumns = parsed
        .filter(col => col !== 'Star')
        .filter((col, idx, arr) => arr.indexOf(col) === idx)
        .filter(col => allowed.has(col));
      if (parsedDataColumns.length > 0) {
        this.displayedColumns = [...parsedDataColumns, 'Star'];
        this.hasStoredColumnsState = true;
      }
    } catch { sessionStorage.removeItem(this.sessionColumnsKey); }
  }

  private normalizeDisplayedColumns() {
    const expectedDataColumns = this.availableColumns.map(col => col.field);
    const expectedSet = new Set(expectedDataColumns);
    const currentDataColumns = this.displayedColumns
      .filter(col => col !== 'Star')
      .filter(col => expectedSet.has(col));
    let normalizedDataColumns = this.hasStoredColumnsState ? [...currentDataColumns] : [...expectedDataColumns];
    if (normalizedDataColumns.length === 0) { normalizedDataColumns = [...expectedDataColumns]; }
    this.displayedColumns = [...normalizedDataColumns, 'Star'];
  }

  private saveSortState() {
    sessionStorage.setItem(this.sessionSortKey, JSON.stringify({ active: this.sortActive, direction: this.sortDirection }));
  }

  private restoreSortState() {
    const stored = sessionStorage.getItem(this.sessionSortKey);
    if (!stored) { return; }
    try {
      const parsed = JSON.parse(stored);
      if (parsed && parsed.active && parsed.direction) { this.sortActive = parsed.active; this.sortDirection = parsed.direction; }
    } catch { sessionStorage.removeItem(this.sessionSortKey); }
  }

  private saveColumnWidths() {
    sessionStorage.setItem(this.sessionColumnWidthsKey, JSON.stringify(this.columnWidths));
  }

  private restoreColumnWidths() {
    const stored = sessionStorage.getItem(this.sessionColumnWidthsKey);
    if (!stored) { this.columnWidths = {}; return; }
    try {
      const parsed = JSON.parse(stored);
      if (!parsed || typeof parsed !== 'object') { return; }
      const validColumns = new Set(this.columnsDefinition.map(col => col.field));
      const restored: { [key: string]: number } = {};
      Object.keys(parsed).forEach(col => {
        if (!validColumns.has(col)) { return; }
        const width = Number(parsed[col]);
        if (!isNaN(width) && width >= 80 && width <= 500) { restored[col] = width; }
      });
      this.columnWidths = restored;
    } catch { sessionStorage.removeItem(this.sessionColumnWidthsKey); this.columnWidths = {}; }
  }

  private refreshGrid() {
    const currentPage = this.pager && this.pager.pager && this.pager.pager.currentPage ? this.pager.pager.currentPage : 1;
    let items = this.applyColumnFilters(this.allItems || []);
    if (this.sortActive && this.sortDirection) { items = this.sortItems([...items], this.sortActive, this.sortDirection); }
    this.pager.setItems(items);
    if (currentPage > 1) { this.pager.setPage(currentPage); }
  }

  private applyColumnFilters(items: PreBillingSearchItem[]): PreBillingSearchItem[] {
    const filters = Object.keys(this.columnFilters || {}).filter(key => !!this.columnFilters[key] && this.columnFilters[key].trim() !== '');
    if (filters.length === 0) { return [...items]; }
    return items.filter(item =>
      filters.every(column => {
        const filterValue = this.normalizeFilterValue(this.columnFilters[column]);
        const displayValue = this.formatCellValue(item, column);
        const itemValue = this.normalizeFilterValue(displayValue);
        return itemValue.indexOf(filterValue) >= 0;
      })
    );
  }

  private sortItems(items: PreBillingSearchItem[], active: string, direction: string): PreBillingSearchItem[] {
    return items.sort((a, b) => {
      const aValue = this.getSortableValue(a, active);
      const bValue = this.getSortableValue(b, active);
      if (aValue < bValue) { return direction === 'asc' ? -1 : 1; }
      if (aValue > bValue) { return direction === 'asc' ? 1 : -1; }
      return 0;
    });
  }

  private normalizeFilterValue(value: string): string {
    const normalized = value ? String(value) : '';
    return normalized.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  private removeResizeListeners() {
    const onMouseMove = (window as any).__preBillingResizeMove;
    const onMouseUp = (window as any).__preBillingResizeUp;
    if (onMouseMove) { document.removeEventListener('mousemove', onMouseMove); (window as any).__preBillingResizeMove = null; }
    if (onMouseUp) { document.removeEventListener('mouseup', onMouseUp); (window as any).__preBillingResizeUp = null; }
  }
}
